import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Award, 
  Clock, 
  TrendingUp, 
  PlayCircle,
  Users,
  Target,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCategories, useUserActivities, useUserCertificates } from '../../hooks';
import { Card, LoadingSpinner, Badge, ProgressBar, Button } from '../../components/common';

const DashboardPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  const { data: activitiesData } = useUserActivities({ limit: 5 });
  const { data: certificatesData } = useUserCertificates();

  const categories = categoriesData?.categories || [];
  const recentActivities = activitiesData?.users || [];
  const certificates = certificatesData?.certificates || [];

  // Calcular estadísticas del usuario
  const totalCategories = categories.length;
  const completedCertificates = certificates.length;
  const completedActivities = recentActivities.filter(a => a.completed).length;
  const progressPercentage = totalCategories > 0 ? (completedCertificates / totalCategories) * 100 : 0;

  // Función helper para obtener el nombre de la categoría
  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Categoría desconocida';
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  if (categoriesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Encabezado de bienvenida */}
      <motion.div {...fadeIn}>
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">
                ¡Bienvenido, {user?.name}!
              </h1>
              <p className="text-primary-100">
                {isAdmin 
                  ? 'Panel de administración - Gestiona la plataforma educativa'
                  : 'Continúa tu aprendizaje en Universidad BrinsAprende'
                }
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Estadísticas rápidas */}
      <motion.div {...fadeIn} transition={{ delay: 0.1 }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Categorías</p>
                <p className="text-2xl font-bold text-gray-900">{totalCategories}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Certificados</p>
                <p className="text-2xl font-bold text-gray-900">{completedCertificates}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Actividades</p>
                <p className="text-2xl font-bold text-gray-900">{completedActivities}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Progreso</p>
                <p className="text-2xl font-bold text-gray-900">{Math.round(progressPercentage)}%</p>
              </div>
            </div>
          </Card>
        </div>
      </motion.div>

      {/* Progreso general */}
      <motion.div {...fadeIn} transition={{ delay: 0.2 }}>
        <Card title="Tu Progreso General" className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between text-sm">
              <span>Módulos completados</span>
              <span>{completedCertificates} de {totalCategories}</span>
            </div>
            <ProgressBar 
              value={completedCertificates} 
              max={totalCategories} 
              showLabel 
              color="primary"
            />
            <p className="text-sm text-gray-600">
              ¡Sigue así! Has completado {completedCertificates} de {totalCategories} módulos disponibles.
            </p>
          </div>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categorías disponibles */}
        <motion.div {...fadeIn} transition={{ delay: 0.3 }}>
          <Card title="Categorías Disponibles">
            <div className="space-y-4">
              {categories.slice(0, 3).map((category) => (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="block p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-primary-600" />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-900">
                          {category.name}
                        </h3>
                        <p className="text-xs text-gray-500">
                          {category.description}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </Link>
              ))}
              
              {categories.length > 3 && (
                <Link to="/home">
                  <Button variant="outline" className="w-full">
                    Ver todas las categorías
                  </Button>
                </Link>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Actividad reciente */}
        <motion.div {...fadeIn} transition={{ delay: 0.4 }}>
          <Card title="Actividad Reciente">
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <PlayCircle className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getCategoryName(activity.categoryId)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.activityType} - {activity.completed ? 'Completado' : 'En progreso'}
                      </p>
                    </div>
                    <Badge 
                      variant={activity.completed ? 'success' : 'warning'}
                      size="sm"
                    >
                      {activity.completed ? 'Completado' : 'En progreso'}
                    </Badge>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">
                    Aún no tienes actividades registradas
                  </p>
                  <Link to="/home" className="mt-2 inline-block">
                    <Button size="sm">
                      Comenzar a aprender
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Acciones rápidas */}
      <motion.div {...fadeIn} transition={{ delay: 0.5 }}>
        <Card title="Acciones Rápidas">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/home">
              <div className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-gray-50 transition-colors text-center">
                <BookOpen className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900">Explorar Categorías</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Descubre todos los módulos disponibles
                </p>
              </div>
            </Link>

            <Link to="/certificates">
              <div className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-gray-50 transition-colors text-center">
                <Award className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900">Mis Certificados</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Ve y descarga tus certificados
                </p>
              </div>
            </Link>

            <Link to="/profile">
              <div className="p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-gray-50 transition-colors text-center">
                <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <h3 className="text-sm font-medium text-gray-900">Mi Perfil</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Actualiza tu información personal
                </p>
              </div>
            </Link>
          </div>
        </Card>
      </motion.div>

      {/* Panel de administrador */}
      {isAdmin && (
        <motion.div {...fadeIn} transition={{ delay: 0.6 }}>
          <Card title="Panel de Administración">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/admin">
                <div className="p-4 rounded-lg bg-primary-50 border border-primary-200 hover:border-primary-300 transition-colors text-center">
                  <TrendingUp className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-primary-900">Dashboard Admin</h3>
                  <p className="text-xs text-primary-600 mt-1">
                    Estadísticas y métricas generales
                  </p>
                </div>
              </Link>

              <Link to="/admin/users">
                <div className="p-4 rounded-lg bg-primary-50 border border-primary-200 hover:border-primary-300 transition-colors text-center">
                  <Users className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-primary-900">Gestión de Usuarios</h3>
                  <p className="text-xs text-primary-600 mt-1">
                    Administrar usuarios y permisos
                  </p>
                </div>
              </Link>

              <Link to="/admin/content">
                <div className="p-4 rounded-lg bg-primary-50 border border-primary-200 hover:border-primary-300 transition-colors text-center">
                  <Target className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                  <h3 className="text-sm font-medium text-primary-900">Gestión de Contenido</h3>
                  <p className="text-xs text-primary-600 mt-1">
                    Administrar productos y categorías
                  </p>
                </div>
              </Link>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default DashboardPage;